/**
 * PETROMIN Customer / Loyalty API (CS) — **server-side** upstream base URL.
 * Must be the real CS host (no trailing slash). Never set this to your Next.js origin
 * (e.g. `http://localhost:3000`), or server proxies will call themselves instead of CS.
 *
 * Override with `CS_API_BASE_URL` or `PETROMIN_CS_API_BASE_URL`.
 * @see https://csapi.petromin.digital
 */
const DEFAULT_CS_API_BASE_URL = "https://csapi.petromin.digital";

const DEFAULT_TENANT_ID = "34e5364d-cd6e-441e-bba8-f1e71793606b";
const DEFAULT_BUSINESS_UNIT_ID = "e48ab8e1-6ec8-470c-b7b8-836501eed882";

function normalizeBaseUrl(url: string): string {
  return url.trim().replace(/\/+$/, "");
}

/** Used for all `apiUrl()` calls from Route Handlers / `src/lib/*Api.ts`. */
export const PETROMIN_CS_API_BASE_URL = normalizeBaseUrl(
 process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || DEFAULT_CS_API_BASE_URL
);

/**
 * Optional public override (e.g. for client-side code that needs the CS host string).
 * Does **not** affect server proxies — those use `PETROMIN_CS_API_BASE_URL` via `apiUrl()`.
 */
export const API_BASE_URL = normalizeBaseUrl(
  process.env.NEXT_PUBLIC_API_BASE_URL?.trim() || DEFAULT_CS_API_BASE_URL
);

/** Tenant ID (`tenant_ID`). Override with `NEXT_PUBLIC_TENANT_ID`. */
export const TENANT_ID =
  process.env.NEXT_PUBLIC_TENANT_ID?.trim() || DEFAULT_TENANT_ID;

/** Business unit ID (`business_unit_ID`). Override with `NEXT_PUBLIC_BUSINESS_UNIT_ID`. */
export const BUSINESS_UNIT_ID =
  process.env.NEXT_PUBLIC_BUSINESS_UNIT_ID?.trim() ||
  DEFAULT_BUSINESS_UNIT_ID;

/** Default CS `workstationId` for `POST /reviews/create` when a page omits `reviewWorkstationId`. */
const DEFAULT_REVIEW_WORKSTATION_ID =
  "0f04b9ef-fa08-4c6b-b9dd-aa78a636c1d5";

export const REVIEW_DEFAULT_WORKSTATION_ID =
  process.env.NEXT_PUBLIC_REVIEW_DEFAULT_WORKSTATION_ID?.trim() ||
  DEFAULT_REVIEW_WORKSTATION_ID;

/** Build an absolute Petromin CS URL for server-side `fetch` (ignores `NEXT_PUBLIC_API_BASE_URL`). */
export function apiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${PETROMIN_CS_API_BASE_URL}${p}`;
}

export function communicationApiUrl(path: string): string {
  const p = path.startsWith("/") ? path : `/${path}`;
  const base = normalizeBaseUrl(
    process.env.NEXT_PUBLIC_COMMUNICATION_API_BASE_URL?.trim() ||
      process.env.NEXT_PUBLIC_COMMUNICATION_BACKEND_API_URL?.trim() ||
      ""
  );
  if (!base) {
    throw new Error(
      "Missing communication API base URL. Set NEXT_PUBLIC_COMMUNICATION_API_BASE_URL or NEXT_PUBLIC_COMMUNICATION_BACKEND_API_URL."
    );
  }
  return `${base}${p}`;
}

/**
 * GoGoMotor master-data base (makes, model-years, models). No trailing slash.
 * Override with `GOGO_MASTER_DATA_BASE_URL` (server) or `NEXT_PUBLIC_GOGO_MASTER_DATA_BASE_URL`.
 */
const DEFAULT_GOGO_MASTER =
  "https://cs.gogomotor.com/backend-api/master-data";

const gogoMasterDataHost =
  process.env.NEXT_PUBLIC_GOGO_MASTER_DATA_BASE_URL?.trim();

export const GOGO_MASTER_DATA_BASE_URL = normalizeBaseUrl(
  gogoMasterDataHost
    ? `${gogoMasterDataHost}/backend-api/master-data`
    : DEFAULT_GOGO_MASTER
);

/**
 * GoGoMotor CMS base used for **news categories** (`fetchCmsNewsCategories`) only.
 * News list/detail use CS `PETROMIN_CS_API_BASE_URL` (`GET /news`, `GET /news/:id`) with tenant headers.
 * Server: `GOGO_CMS_NEWS_BEARER_TOKEN` (required for legacy CMS category routes when used).
 * Optional: `NEXT_PUBLIC_GOGO_CMS_NEWS_BU` (default `2`). Relative news images use `NEXT_PUBLIC_OCI_PETROMIN_IMAGE_CDN_BASE_URL` (see `resolveNewsImageUrl`).
 */
const DEFAULT_GOGO_CMS_NEWS = "https://cmsapidev.gogomotor.com";

export const GOGO_CMS_NEWS_BASE_URL = normalizeBaseUrl(
  process.env.NEXT_PUBLIC_API_BASE_URL?.trim() ||
    DEFAULT_GOGO_CMS_NEWS
);
